import React, { useEffect } from 'react';
import { FiBell, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';
import { FaEnvelope, FaSms, FaBell, FaClock, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNotifications } from '../../provider/notification.provider';

const NotificationsList: React.FC = () => {
    const {
        notifications,
        unreadCount,
        loading,
        fetchNotifications,
        markAsRead,
        markAllAsRead
    } = useNotifications();

    useEffect(() => {
        // Fetch notifications on mount (will be no-op if NOTIFICATIONS_ENABLED is false)
        fetchNotifications(50);
    }, [fetchNotifications]);

    const isUnread = (status: string) => status !== 'read';

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'email': return <FaEnvelope className="w-4 h-4 text-blue-600" />;
            case 'sms': return <FaSms className="w-4 h-4 text-green-600" />;
            case 'push': return <FaBell className="w-4 h-4 text-purple-600" />;
            default: return <FiBell className="w-4 h-4 text-gray-600" />;
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'sent':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <FaCheckCircle className="w-3 h-3" /> Sent
                    </span>
                );
            case 'pending':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <FaClock className="w-3 h-3" /> Pending
                    </span>
                );
            case 'failed':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <FaTimesCircle className="w-3 h-3" /> Failed
                    </span>
                );
            case 'delivered':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <FaCheckCircle className="w-3 h-3" /> Delivered
                    </span>
                );
            case 'read':
                return (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <FaCheckCircle className="w-3 h-3" /> Read
                    </span>
                );
            default: return null;
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleString();
    };

    return (
        <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900">My Notifications</h2>
                    <div className="flex gap-2">
                        {unreadCount > 0 && (
                            <button
                                onClick={() => markAllAsRead()}
                                className="px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors flex items-center gap-2"
                            >
                                <FiCheckCircle className="w-4 h-4" />
                                Mark All Read
                            </button>
                        )}
                        <button
                            onClick={() => fetchNotifications(50)}
                            className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded transition-colors flex items-center gap-2"
                        >
                            <FiRefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {/* Notification List */}
            <div className="divide-y divide-gray-200">
                {loading ? (
                    <div className="px-6 py-12 flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="px-6 py-12 text-center text-gray-500">
                        <FiBell className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                        <p>No notifications yet</p>
                        <p className="text-sm text-gray-400 mt-2">
                            You'll see your notifications here when they arrive
                        </p>
                    </div>
                ) : (
                    notifications.map((notification) => (
                        <div
                            key={notification.id}
                            className={`px-6 py-4 hover:bg-gray-50 transition-colors ${
                                isUnread(notification.status) ? 'bg-blue-50/30 border-l-4 border-l-blue-500' : ''
                            }`}
                        >
                            <div className="flex items-start gap-4">
                                <div className="flex-shrink-0 mt-1">
                                    {getTypeIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-sm font-semibold text-gray-900">
                                            {notification.title || notification.subject || 'Notification'}
                                        </h3>
                                        {getStatusBadge(notification.status)}
                                        {isUnread(notification.status) && (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                New
                                            </span>
                                        )}
                                    </div>
                                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-600">{notification.message}</p>
                                    <p className="text-xs text-gray-400 mt-2">
                                        {formatDate(notification.sent_at || notification.created_at)}
                                    </p>
                                </div>
                                <div className="flex-shrink-0">
                                    {isUnread(notification.status) && (
                                        <button
                                            onClick={() => markAsRead(notification.id)}
                                            className="text-sm text-blue-600 hover:text-blue-800"
                                        >
                                            Mark as read
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default NotificationsList;
